/**
 * Transient confirmation for things that changed on the server.
 *
 * Deliberately not a place for errors that need a decision — those stay inline
 * next to the control, where the person can act on them. This is for outcomes
 * you acknowledge and move on from.
 */
import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

export type ToastTone = 'ok' | 'danger' | 'warn' | 'info';

export interface Toast {
	id: string;
	tone: ToastTone;
	title: string;
	message?: string;
}

/** Long enough to read a sentence; errors linger because they matter more. */
export const DWELL: Record<ToastTone, number> = {
	ok: 4000,
	info: 5000,
	warn: 7000,
	danger: 9000,
};

const slice = createSlice({
	name: 'toast',
	initialState: { items: [] as Toast[] },
	reducers: {
		pushToast: {
			reducer(state, action: PayloadAction<Toast>) {
				/* At most four on screen, same as the provider did. */
				state.items = [...state.items.slice(-3), action.payload];
			},
			prepare(input: {
				tone: ToastTone;
				title: string;
				message?: string;
			}) {
				return { payload: { id: nanoid(), ...input } };
			},
		},
		dismissToast(state, action: PayloadAction<string>) {
			state.items = state.items.filter(
				(item) => item.id !== action.payload,
			);
		},
	},
});

export const { pushToast, dismissToast } = slice.actions;
export default slice.reducer;
